import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { useRegister } from '@/api/auth/register';
import { isSessionValid, saveAuthContext } from '@/utils/router-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/ui/password-input';
import { storage } from '@/utils/storage';
import { useGetInvitationByToken } from '@/api/organizations/get-invitation-by-token';

const registerSearchSchema = z.object({
  invitationToken: z.string().optional()
});

export const Route = createFileRoute('/register')({
  validateSearch: registerSearchSchema,
  component: RegisterPage
});

function RegisterPage() {
  const navigate = useNavigate();
  const { invitationToken } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const registerMutation = useRegister();

  const { data: invitation } = useGetInvitationByToken(invitationToken || null);

  useEffect(() => {
    if (isSessionValid()) {
      navigate({ to: '/ideas' });
    }
  }, [navigate]);

  const isPasswordValid = useMemo(() => {
    const hasMinLength = password.length >= 10;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSymbol = /[^a-zA-Z0-9]/.test(password);
    return hasMinLength && hasUppercase && hasLowercase && hasSymbol;
  }, [password]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    registerMutation.mutate(
      { email, password, invitationToken },
      {
        onSuccess: async (data) => {
          saveAuthContext(
            {
              email: data.email,
              firstName: '',
              lastName: '',
              company: '',
              role: data.role,
              defaultOrganizationId: data.defaultOrganizationId
            },
            {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              accessTokenValidUntil: data.accessTokenValidUntil,
              refreshTokenValidUntil: data.refreshTokenValidUntil
            }
          );

          if (data.defaultProjectId) {
            storage.setProjectId(data.defaultProjectId);
            storage.setHasCompletedOnboarding(true);
          }

          if (invitationToken) {
            toast.success(`Bienvenue ! Vous avez rejoint l'organisation ${invitation?.organizationName || ''}`);
          }
          await navigate({ to: '/onboarding' });
        },
        onError: () => {
          toast.error("Echec de l'inscription", {
            description: 'Impossible de creer le compte. Verifiez vos informations.'
          });
        }
      }
    );
  };

  return (
    <div className="h-dvh bg-linear-to-b from-background to-muted/20 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-linear-to-br from-primary to-secondary rounded-lg">
              <Sparkles className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
              Créer un compte BeSpark
            </span>
          </CardTitle>
          <CardDescription>
            {invitation
              ? `Créer un compte pour rejoindre ${invitation.organizationName}`
              : 'Rejoignez la plateforme pour collecter et prioriser vos feedbacks'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitation && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Vous allez créer un compte et rejoindre l'organisation <strong>{invitation.organizationName}</strong>
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput
                id="password"
                placeholder="Mot de passe (10+ caracteres, maj, min, symbole)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Min. 10 caracteres, au moins une majuscule, une minuscule et un symbole.
              </p>
            </div>
            <Button
              type="submit"
              variant="hero"
              className="w-full"
              disabled={registerMutation.isPending || !isPasswordValid || !email}
            >
              {registerMutation.isPending ? 'Creation en cours...' : 'Creer mon compte'}
            </Button>
          </form>
          <div className="text-center text-sm text-muted-foreground mt-4">
            Deja un compte ?{' '}
            <Link
              to="/login"
              search={invitationToken ? { invitationToken } : undefined}
              className="text-primary hover:underline"
            >
              Se connecter
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
